import { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { useConnectivity } from '@/hooks/useConnectivity';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import { syncQueueService } from '@/services/offline/syncQueue';
import { cacheReader } from '@/services/offline/cacheReader';
import { getOriginalRequest } from '@/services/offline/offlineMiddleware';
import { useCalendarStore } from '@/stores/calendarStore';

function formatDay(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  return isNaN(d) ? dateStr : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function OfflineStatusBar() {
  const { isOnline, isRecovering } = useConnectivity();
  const { pendingCount, failedCount, isSyncing, totalPending } = useSyncStatus();
  const calendarError = useCalendarStore((s) => s.lastError);
  const [showSuccess, setShowSuccess] = useState(false);
  const [prevPending, setPrevPending] = useState(0);
  const [partial, setPartial] = useState(cacheReader.lastPartial);

  // Flash success briefly when sync completes
  useEffect(() => {
    if (prevPending > 0 && totalPending === 0 && failedCount === 0 && isOnline) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
    setPrevPending(totalPending);
  }, [totalPending, failedCount, isOnline]);

  useEffect(() => cacheReader.subscribePartial(setPartial), []);

  const handleRetry = () => {
    syncQueueService.retryFailed();
    const originalRequest = getOriginalRequest();
    if (originalRequest) {
      syncQueueService.processQueue(originalRequest);
    }
  };

  // Hidden when online with nothing pending
  if (isOnline && !isSyncing && failedCount === 0 && !showSuccess) {
    return null;
  }

  // Success flash
  if (showSuccess) {
    return (
      <div className="bg-green-50 border-b border-green-200 px-4 py-2 flex items-center gap-2 text-sm text-green-700">
        <CheckCircle className="w-4 h-4" />
        <span>All changes synced</span>
      </div>
    );
  }

  // Failed state
  if (failedCount > 0 && isOnline) {
    return (
      <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between text-sm text-red-700">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{failedCount} change{failedCount > 1 ? 's' : ''} failed to sync</span>
        </div>
        <button
          onClick={handleRetry}
          className="px-3 py-1 text-xs font-medium bg-red-100 hover:bg-red-200 rounded-md transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Backend reachable again, but this client is held in offline mode until
  // its queued changes have been replayed (see the reconnect gate).
  if (!isOnline && isRecovering) {
    return (
      <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center gap-2 text-sm text-yellow-700">
        <RefreshCw className="w-4 h-4 flex-shrink-0" />
        <span>Connection restored. Your offline changes will sync shortly. Until then you are working from saved data.</span>
        {totalPending > 0 && (
          <span className="ml-auto text-xs bg-yellow-100 px-2 py-0.5 rounded-full flex-shrink-0">
            {totalPending} pending
          </span>
        )}
      </div>
    );
  }

  // Offline state
  if (!isOnline) {
    // Surface the cached-window boundary when the calendar asked for dates
    // the cache cannot serve (fully or partially outside the window).
    const windowRange = partial?.window;
    const showWindowNotice = Boolean(calendarError?.offline || partial);

    return (
      <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center gap-2 text-sm text-yellow-700">
        <WifiOff className="w-4 h-4 flex-shrink-0" />
        <span>
          {showWindowNotice && windowRange
            ? `You are offline. The schedule is available for ${formatDay(windowRange.start)} – ${formatDay(windowRange.end)}; other dates cannot be shown until connection returns.`
            : showWindowNotice
              ? 'You are offline. Some dates are outside the saved schedule and cannot be shown until connection returns.'
              : 'You are offline. Changes will sync when connection returns.'}
        </span>
        {totalPending > 0 && (
          <span className="ml-auto text-xs bg-yellow-100 px-2 py-0.5 rounded-full flex-shrink-0">
            {totalPending} pending
          </span>
        )}
      </div>
    );
  }

  // Syncing state
  if (isSyncing) {
    return (
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center gap-2 text-sm text-blue-700">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>Syncing {totalPending} change{totalPending > 1 ? 's' : ''}...</span>
      </div>
    );
  }

  return null;
}
